// File: frontend/src/utils/imageDownscale.ts

/**
 * Client-side image downscaling, applied right before an upload starts.
 *
 * A modern phone camera produces 3–8 MB JPEGs. On a slow mobile connection
 * that is 30+ seconds of waiting for a file the backend immediately throws
 * away: ImageProcessor already re-encodes every content image to 1200px at
 * JPEG 0.82. Shrinking on the client first therefore costs no quality at all
 * and cuts the transfer to a fraction of the original size.
 *
 * Every failure path falls back to the untouched original file — a broken
 * canvas, an exotic format or an out-of-memory error must never block the
 * upload itself.
 */

/** Longest edge (px) the uploaded image is reduced to. Kept above the
    backend's own 1200px target so the server still does the final resize
    and nothing is upscaled. */
const MAX_EDGE = 1600;

/** JPEG quality of the re-encoded file. Above the backend's 0.82 so this
    intermediate step is not the one that visibly degrades the image. */
const JPEG_QUALITY = 0.85;

/** Files below this size are sent as-is — re-encoding them saves nothing
    and would only strip quality for no benefit. */
const SKIP_BELOW_BYTES = 400 * 1024;

/**
 * Decode a file into something drawable. createImageBitmap is preferred
 * because it applies the EXIF orientation for us; without it, photos taken
 * in portrait mode come out rotated.
 */
const decode = async (file: File): Promise<ImageBitmap | HTMLImageElement> => {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      // Older Safari rejects the options object — fall through to <img>
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("DECODE_FAILED"));
    };
    img.src = url;
  });
};

/**
 * Returns a downscaled JPEG version of the file, or the original file when
 * downscaling is unnecessary or impossible.
 */
export const downscaleImage = async (file: File): Promise<File> => {
  // GIFs would lose their animation, and small files gain nothing
  if (file.type === "image/gif" || file.size <= SKIP_BELOW_BYTES) {
    return file;
  }

  try {
    const source = await decode(file);
    const width = "width" in source ? source.width : 0;
    const height = "height" in source ? source.height : 0;
    if (!width || !height) return file;

    const longestEdge = Math.max(width, height);
    // Already small enough — re-encoding would only lose quality
    if (longestEdge <= MAX_EDGE) {
      if ("close" in source) source.close();
      return file;
    }

    const scale = MAX_EDGE / longestEdge;
    const targetWidth = Math.round(width * scale);
    const targetHeight = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return file;

    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(source as CanvasImageSource, 0, 0, targetWidth, targetHeight);
    if ("close" in source) source.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY)
    );
    if (!blob) return file;

    // A "downscale" that produced a bigger file is not a downscale
    if (blob.size >= file.size) return file;

    // Keep the original base name so the server-side name stays recognisable
    const baseName = file.name.replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
};